import {
    filterCustomSearch,
    firstLetterCapitalize,
    generateTextError,
    swal,
    useAPIRest,
    verifyHttpCode
} from "@/common/constants";
import {updateValues} from "@/layouts/main";

/**
 Esta función se encarga de listar los datos de una colección a través de una API REST.
 @async
 @param {string} urlAPI - La URL de la API REST que se utilizará para obtener los datos.
 @param {Object|function} newUpdateValues - Objeto o función del framework VueJS que se utiliza para actualizar los valores reactivos en la interfaz de usuario.
 @param {string} tokenUpdated - El token actualizado que se utilizará para autenticar la solicitud.
 @param {number} [page=1] - El número de página que se utilizará para obtener los datos paginados.
 @param {string} [orderBy='created_at'] - La columna por la que se ordenarán los datos.
 @param {string} [order='ASC'] - El orden en que se mostrarán los datos.
 @param {string} [criterion=''] - El criterio que se utilizará para filtrar los datos.
 @param {string} [search=''] - La cadena de búsqueda que se utilizará para filtrar los datos.
 @param {string} [where=''] - La sentencia Where que se utilizara para filtrar los datos.
 @param {boolean} [isPaginate=true] - Indica si se utilizará paginación en la lista de datos.
 @param {Object} [customQuery={}] - Los parámetros personalizados que se utilizarán para realizar la solicitud.
 @param {Object} [customQuery.keys] - La clave que se utilizará para filtrar los datos.
 @param {Object} [customQuery.values] - El valor que se utilizará para filtrar los datos.
 @returns {Promise<Object>} - Una promesa que resuelve en un objeto con los datos de la colección solicitada o un mensaje de error.
 */
export const listCollectionData = async (urlAPI, newUpdateValues, tokenUpdated, page = 1, orderBy = 'created_at', order = 'ASC', criterion = '', search = '', where = '', isPaginate = true, customQuery = {}) => {
    search = filterCustomSearch(criterion, search);
    return await useAPIRest(urlAPI, 'GET', tokenUpdated, undefined, {
        page,
        orderBy,
        order,
        criterion,
        search,
        where,
        isPaginate,
        ...customQuery
    }).then(resJSON => {
        const {statusCode} = resJSON;
        const textError = generateTextError(resJSON);
        if (verifyHttpCode(statusCode, 'success')) {
            if (newUpdateValues.constructor.name === 'Object')
                for (const key in newUpdateValues) {
                    let newValue;
                    if (key === 'paginate') newValue = resJSON.meta;
                    else newValue = 'data' in resJSON ? resJSON.data : resJSON;
                    updateValues({[key]: newValue}, newUpdateValues);
                }
            else {
                newUpdateValues('updateValues', {'paginate': resJSON.meta});
                newUpdateValues('updateValues', {'collectionData': resJSON.data});
            }
        } else swal.fire(textError, '', 'error');
        return resJSON;
    });
};

/**
 @function enableDisableData
 @async
 @param {object} entity - Una entidad que se construye de la siguiente manera: {gender: 'm', value: 'banco'}.
 @param {string} accion - El nombre de la acción a realizarse, si se quiere activar o inactivar un dato.
 @param {object} data - Un objeto que contiene la información de un registro de la base de datos.
 @param {string} urlAPI - La url de la API.
 @param {string | object} authToken - El token de autenticación de usuario.
 @param {function} [customEnableDisableData] - Un callback que se utilizara primero en el caso de que se desee aplicar un proceso diferente al predeterminado.
 @param {string} [primaryKey='id'] - La clave primaria de la entidad.
 @returns {Promise} - Una promesa que resuelve en la respuesta JSON de la API.
 @throws {Error} - Si ocurre algún error en la ejecución de la función.
 */
export const enableDisableData = async (entity, accion, data, urlAPI, authToken, customEnableDisableData = undefined, primaryKey = "id", customAlertUsuario = '') => {
    if (customEnableDisableData) customEnableDisableData();
    else {
        const entityLowerCase = entity.value.toLowerCase();
        const textoExitoso = `¡${firstLetterCapitalize(entityLowerCase)} ` + (accion === 'activar' ? (entity.gender.toLowerCase() === 'f' ? 'activada' : 'activado') : (entity.gender.toLowerCase() === 'f' ? 'desactivada' : 'desactivado')) + ' con exito!'  + customAlertUsuario;
        return await swal.fire({
            title: `¿Esta seguro de ${accion} ${entity.gender.toLowerCase() === 'f' ? 'esta' : 'este'} ${entityLowerCase}?`,
            icon: 'warning',
            showCloseButton: true,
            showCancelButton: true,
            focusConfirm: false,
            confirmButtonText: firstLetterCapitalize(accion),
            cancelButtonText: 'Cancelar'
        }).then(async (result) => {
            if (result.isConfirmed) {
                await useAPIRest(urlAPI.replace(':' + primaryKey, data[primaryKey]), 'PATCH', authToken.constructor.name === 'RefImpl' ? authToken.value : authToken).then(resJSON => {
                    const {statusCode} = resJSON;
                    delete resJSON.statusCode;
                    const textError = generateTextError(resJSON);
                    if (verifyHttpCode(statusCode, 'success')) swal.fire(textoExitoso, '', 'success').then(data.estado = resJSON.estado);
                    else swal.fire(textError, '', 'error');
                })
            }
        });
    }
}